import {
  createClientSharedComposable,
  getStore,
  useLocalStorageObject,
} from '@global-store/store'
import type {
  SearchStore,
  Suggestions,
} from '@shared/global-store/stores/search/types'
import { defineStore } from 'pinia'

const SUGGESTIONS_LOCALE_STORAGE_KEY = 'shopify-search-suggestions'

export const useSearchStore = createClientSharedComposable(() =>
  defineStore('search', {
    state: (): SearchStore => ({
      query: null,
      suggestions: useLocalStorageObject<Suggestions | null>(
        SUGGESTIONS_LOCALE_STORAGE_KEY,
        null
      ),
      pageIndex: 0,
      cursor: { backward: undefined, forward: undefined },
      isOpen: false,
      seenProducts: 0,
    }),
    getters: {
      isLoading: (state) => state.query === null,
      hasQuery: (state) => !!state.query,
      isSearchOpen: (state) => state.isOpen,
      showSuggestions: (state) => {
        return state.query && state.query.length >= 3
      },
      getSuggestions: (state) => {
        if (state.suggestions === null) {
          return []
        }

        return [
          ...state.suggestions.content.slice(0, 2),
          ...state.suggestions.completions.slice(0, 5),
        ]
      },
    },
    actions: {
      setQuery(query: string | null) {
        this.query = query
      },
      setSuggestions(suggestions: Suggestions) {
        this.suggestions = suggestions
      },
      toggleOpenState() {
        this.isOpen = !this.isOpen
      },
      setNewCursor(
        cursor: string | undefined,
        variant: 'backward' | 'forward'
      ) {
        this.cursor[variant] = cursor
      },
      setNewPageIndex(pageIndex: number) {
        this.pageIndex = pageIndex
      },
      setSeenProducts(seenProducts: number) {
        this.seenProducts = seenProducts
      },
      $reset() {
        this.query = null
        localStorage.removeItem(SUGGESTIONS_LOCALE_STORAGE_KEY)
      },
    },
    hydrate: (storeState) => {
      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.suggestions = useLocalStorageObject<Suggestions | null>(
        SUGGESTIONS_LOCALE_STORAGE_KEY,
        null
      )
    },
  })(getStore())
)
